import { notify } from '@/lib/toast';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, CalendarRange, Lock, Unlock } from 'lucide-react';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate } from '@/lib/utils';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

interface FiscalPeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isClosed: boolean;
  closedAt: string | null;
  _count?: { journalEntries: number };
}

type FiscalPeriodsResponse = FiscalPeriod[] | { data?: FiscalPeriod[]; total?: number };

const defaultForm ={ name: '', startDate: '', endDate: '' };

export function FiscalPeriodsPage() {
  const { t } = useTranslation('finance');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('accounting:create') || hasPermission('accounting:*');
  const canUpdate = hasPermission('accounting:update') || hasPermission('accounting:*');
  const canDelete = hasPermission('accounting:delete') || hasPermission('accounting:*');

  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<FiscalPeriod | null>(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const [closeConfirm, setCloseConfirm] = useState<{ open: boolean; id: string; name: string; closing: boolean }>({ open: false, id: '', name: '', closing: true });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (dialogOpen && editItem) {
      setForm({
        name: editItem.name ?? '',
        startDate: editItem.startDate ? editItem.startDate.slice(0, 10) : '',
        endDate: editItem.endDate ? editItem.endDate.slice(0, 10) : '',
      });
    } else if (dialogOpen) {
      setForm({ ...defaultForm });
    }
  }, [dialogOpen, editItem]);

  const { data, isLoading } = useQuery<FiscalPeriodsResponse>({
    queryKey: ['fiscal-periods', page],
    queryFn: async () => {
      const res = await api.get('/accounting/fiscal-periods', { params: { page, limit } });
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { name: form.name, startDate: form.startDate, endDate: form.endDate };
      if (editItem) {
        const { data } = await api.patch(`/accounting/fiscal-periods/${editItem.id}`, payload);
        return data;
      }
      const { data } = await api.post('/accounting/fiscal-periods', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(editItem ? 'Fiscal period updated' : 'Fiscal period created');
      queryClient.invalidateQueries({ queryKey: ['fiscal-periods'] });
      setDialogOpen(false);
    },
    onError: (err) => notify.error(err, 'Save failed'),
  });

  const closeMutation = useMutation({
    mutationFn: ({ id, closing }: { id: string; closing: boolean }) =>
      api.post(`/accounting/fiscal-periods/${id}/${closing ? 'close' : 'reopen'}`),
    onSuccess: (_res, vars) => {
      toast.success(vars.closing ? 'Fiscal period closed' : 'Fiscal period reopened');
      queryClient.invalidateQueries({ queryKey: ['fiscal-periods'] });
    },
    onError: (err) => notify.error(err, 'Action failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/accounting/fiscal-periods/${id}`),
    onSuccess: () => {
      toast.success('Fiscal period deleted');
      queryClient.invalidateQueries({ queryKey: ['fiscal-periods'] });
    },
    onError: (err) => notify.error(err, 'Delete failed'),
  });

  const list: FiscalPeriod[] = Array.isArray(data) ? data : data?.data ?? [];
  const total: number = (Array.isArray(data) ? undefined : data?.total) ?? list.length;

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title="Fiscal Periods"
        subtitle="Define accounting periods and lock the books once a period is finalised"
        action={
          canCreate ? (
            <Button variant="gradient" onClick={() => { setEditItem(null); setDialogOpen(true); }}>
              <Plus className="me-2 h-4 w-4" />
              Add Period
            </Button>
          ) : undefined
        }
      />

      <Card className="animate-fade-in-up">
        <CardContent className="p-0">
          <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th>{t('name')}</Th>
                  <Th>{t('colStart')}</Th>
                  <Th>{t('colEnd')}</Th>
                  <Th className="text-center">{t('colEntries')}</Th>
                  <Th className="text-center">{t('status')}</Th>
                  <Th className="text-end">{t('actions')}</Th>
                </Tr>
              </THead>
              <TBody>
                {isLoading ? (
                  <Tr hover={false} className="no-card">
                    <Td colSpan={6} className="py-8">
                      <SkeletonTable rows={5} cols={6} />
                    </Td>
                  </Tr>
                ) : list.length === 0 ? (
                  <Tr hover={false} className="no-card">
                    <Td colSpan={6} className="p-0">
                      <EmptyState
                        icon={CalendarRange}
                        title="No fiscal periods"
                        description="Create a fiscal period to organise journal entries and lock finalised books"
                        action={canCreate ? { label: 'Add Period', onClick: () => { setEditItem(null); setDialogOpen(true); } } : undefined}
                      />
                    </Td>
                  </Tr>
                ) : (
                  list.map((item) => {
                    const entryCount = item._count?.journalEntries ?? 0;
                    return (
                      <Tr key={item.id}>
                        <Td className="font-medium td-name">{item.name}</Td>
                        <Td label={t('colStart')} className="text-muted-foreground">{formatDate(item.startDate)}</Td>
                        <Td label={t('colEnd')} className="text-muted-foreground">{formatDate(item.endDate)}</Td>
                        <Td label={t('colEntries')} className="text-center">{entryCount}</Td>
                        <Td label={t('status')} className="text-center">
                          {item.isClosed ? (
                            <StatusBadge status="CLOSED" tone="danger" label={t('periodStatusClosed')} icon={<Lock className="h-3 w-3" />} />
                          ) : (
                            <StatusBadge status="OPEN" tone="success" label={t('periodStatusOpen')} icon={<Unlock className="h-3 w-3" />} />
                          )}
                        </Td>
                        <Td className="text-end td-actions">
                          <TableRowActions
                            actions={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                icon: Edit,
                                onSelect: () => { setEditItem(item); setDialogOpen(true); },
                                permission: canUpdate && !item.isClosed,
                                primary: true,
                              },
                              {
                                key: 'toggle',
                                label: item.isClosed ? 'Reopen period' : 'Close period',
                                icon: item.isClosed ? Unlock : Lock,
                                onSelect: () => setCloseConfirm({ open: true, id: item.id, name: item.name, closing: !item.isClosed }),
                                permission: canUpdate,
                                primary: true,
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                icon: Trash2,
                                onSelect: () => setDeleteConfirm({ open: true, id: item.id, name: item.name }),
                                permission: canDelete && entryCount === 0,
                                variant: 'destructive',
                              },
                            ]}
                          />
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </TBody>
            </Table>
        </CardContent>
      </Card>

      {total > limit && (
        <div className="border-t px-4 py-3 mt-1">
          <PaginationBar page={page} pageSize={limit} total={total} onPageChange={setPage} />
        </div>
      )}

      <FormDialog
        open={dialogOpen}
        title={editItem ? 'Edit Fiscal Period' : 'Add Fiscal Period'}
        submitLabel={editItem ? 'Update Period' : 'Create Period'}
        isPending={saveMutation.isPending}
        onClose={() => setDialogOpen(false)}
        onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}
        submitDisabled={!form.name || !form.startDate || !form.endDate}
      >
        <div>
          <Label>Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder='e.g. "FY 2025-26", "Jul 2025"'
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Start Date *</Label>
            <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} required />
          </div>
          <div>
            <Label>End Date *</Label>
            <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} required />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Periods cannot overlap. Once closed, a period is locked against edits; reopen it to make changes.
        </p>
      </FormDialog>

      <ConfirmDialog
        open={closeConfirm.open}
        title={closeConfirm.closing ? 'Close Fiscal Period' : 'Reopen Fiscal Period'}
        message={
          closeConfirm.closing
            ? `Close "${closeConfirm.name}"? This locks the period so its books can no longer be edited.`
            : `Reopen "${closeConfirm.name}"? This unlocks the period for further changes.`
        }
        confirmLabel={closeConfirm.closing ? 'Close Period' : 'Reopen Period'}
        variant={closeConfirm.closing ? 'danger' : 'warning'}
        loading={closeMutation.isPending}
        onConfirm={() => {
          closeMutation.mutate(
            { id: closeConfirm.id, closing: closeConfirm.closing },
            { onSettled: () => setCloseConfirm({ open: false, id: '', name: '', closing: true }) },
          );
        }}
        onCancel={() => setCloseConfirm({ open: false, id: '', name: '', closing: true })}
      />

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Fiscal Period"
        message={`Delete "${deleteConfirm.name}"? This cannot be undone. Periods with journal entries cannot be deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSettled: () => setDeleteConfirm({ open: false, id: '', name: '' }),
          });
        }}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
